import { Announcement, getAnnouncements } from "./announcement";
import { escapeHTML, isLocalStorageAvailable } from "../util/html-helpers";

const STORAGE_PREFIX = "rwt_announcement_dismissed_";
const MODAL_ID = "announcement-modal";
const BACKDROP_ID = "announcement-modal-backdrop";
const DISPLAY_DELAY = 500; // 500ms delay

let previouslyFocusedElement: HTMLElement | null = null;
let currentAnnouncements: Announcement[] = [];

// Filter announcements by date range
export function filterActiveAnnouncements(
  announcements: Announcement[] | null,
): Announcement[] {
  if (announcements === null) {
    return [];
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return announcements.filter((announcement) => {
    try {
      const startDate = new Date(announcement.startDate);
      const endDate = new Date(announcement.endDate);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      return today >= startDate && today <= endDate;
    } catch (e) {
      console.error("Invalid date in announcement:", announcement.id, e);
      return false;
    }
  });
}

// Check if announcement has been dismissed
function isDismissed(announcementId: string, endDate: Date): boolean {
  if (!isLocalStorageAvailable()) {
    return false;
  }

  const key = STORAGE_PREFIX + announcementId;
  const dismissedUntil = localStorage.getItem(key);
  if (!dismissedUntil) {
    return false;
  }

  try {
    const expiryDate = new Date(dismissedUntil);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return today <= expiryDate;
  } catch (e) {
    console.error("Invalid date in localStorage for:", key, e);
    return false;
  }
}

// Get announcements that haven't been dismissed
function getUndismissedAnnouncements(
  activeAnnouncements: Announcement[],
): Announcement[] {
  return activeAnnouncements.filter((ann) => !isDismissed(ann.id, ann.endDate));
}

// Dismiss an announcement
function dismissAnnouncement(announcementId: string, endDate: Date): void {
  if (!isLocalStorageAvailable()) {
    return;
  }

  const key = STORAGE_PREFIX + announcementId;
  localStorage.setItem(key, endDate.toISOString());
}

// Clean up expired dismissals from localStorage
function cleanupExpiredDismissals(): void {
  if (!isLocalStorageAvailable()) {
    return;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const keysToRemove: string[] = [];

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) {
        const value = localStorage.getItem(key);
        if (value) {
          const expiryDate = new Date(value);
          if (today > expiryDate) {
            keysToRemove.push(key);
          }
        }
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (e) {
    console.error("Error cleaning up expired dismissals:", e);
  }
}

// Build modal HTML
function buildModalHTML(announcements: Announcement[]): string {
  const announcementsHTML = announcements
    .map((ann, index) => {
      const linkHTML =
        ann.link && ann.linkText
          ? `<p class="announcement-link">
                <a href="${escapeHTML(
                  ann.link,
                )}" target="_blank" rel="noopener noreferrer">
                    ${escapeHTML(ann.linkText)}
                </a>
            </p>`
          : "";

      const dividerHTML =
        index < announcements.length - 1
          ? '<hr class="announcement-divider" />'
          : "";

      return `
            <div class="announcement-item" data-id="${escapeHTML(ann.id)}">
                <h3 class="announcement-title">${escapeHTML(ann.title)}</h3>
                <p class="announcement-message">${escapeHTML(ann.message)}</p>
                ${linkHTML}
            </div>
            ${dividerHTML}
        `;
    })
    .join("");

  return `
        <div id="${BACKDROP_ID}" class="announcement-modal-backdrop"></div>
        <div id="${MODAL_ID}" class="announcement-modal" role="dialog"
             aria-modal="true" aria-labelledby="announcement-modal-title">
            <div class="announcement-modal-content">
                <h2 id="announcement-modal-title" class="sr-only">Announcements</h2>
                <div class="announcement-list">
                    ${announcementsHTML}
                </div>
                <div class="announcement-actions">
                    <button id="announcement-ok-btn" class="announcement-ok-btn">OK</button>
                </div>
            </div>
        </div>
    `;
}

// Show the modal
function showModal(): void {
  const backdrop = document.getElementById(BACKDROP_ID);
  const modal = document.getElementById(MODAL_ID);

  if (!backdrop || !modal) {
    return;
  }

  // Store currently focused element
  previouslyFocusedElement = document.activeElement as HTMLElement;

  // Show modal after delay
  setTimeout(() => {
    backdrop.classList.add("visible");
    modal.classList.add("visible");

    // Focus the OK button
    const okButton = document.getElementById("announcement-ok-btn");
    if (okButton) {
      okButton.focus();
    }
  }, DISPLAY_DELAY);
}

// Close the modal
function closeModal(): void {
  const backdrop = document.getElementById(BACKDROP_ID);
  const modal = document.getElementById(MODAL_ID);

  if (backdrop) {
    backdrop.classList.remove("visible");
  }
  if (modal) {
    modal.classList.remove("visible");
  }

  // Dismiss all current announcements
  currentAnnouncements.forEach((ann) => {
    dismissAnnouncement(ann.id, ann.endDate);
  });

  // Restore focus
  if (previouslyFocusedElement) {
    previouslyFocusedElement.focus();
  }

  // Remove modal from DOM after animation
  setTimeout(() => {
    if (backdrop && backdrop.parentNode) {
      backdrop.parentNode.removeChild(backdrop);
    }
    if (modal && modal.parentNode) {
      modal.parentNode.removeChild(modal);
    }
  }, 300);
}

// Setup event listeners for the modal
function setupEventListeners(): void {
  // OK button click
  const okButton = document.getElementById("announcement-ok-btn");
  if (okButton) {
    okButton.addEventListener("click", closeModal);
  }

  // Click outside modal (on backdrop)
  const backdrop = document.getElementById(BACKDROP_ID);
  if (backdrop) {
    backdrop.addEventListener("click", function (event) {
      if (event.target === backdrop) {
        closeModal();
      }
    });
  }

  // Escape key
  const handleEscape = (event: KeyboardEvent): void => {
    if (event.key === "Escape") {
      const modal = document.getElementById(MODAL_ID);
      if (modal && modal.classList.contains("visible")) {
        closeModal();
        document.removeEventListener("keydown", handleEscape);
      }
    }
  };
  document.addEventListener("keydown", handleEscape);
}

// Initialize the announcement system
async function init(): Promise<void> {
  try {
    // Clean up expired dismissals
    cleanupExpiredDismissals();

    // Load and filter announcements
    const announcements = await getAnnouncements();
    const activeAnnouncements = filterActiveAnnouncements(announcements);
    const undismissedAnnouncements =
      getUndismissedAnnouncements(activeAnnouncements);

    // Check if at least one announcement has popup: true
    const hasPopupAnnouncement = undismissedAnnouncements.some(
      (ann) => ann.popup === true,
    );

    // If no announcements to show or none have popup: true, exit
    if (undismissedAnnouncements.length === 0 || !hasPopupAnnouncement) {
      return;
    }

    // Sort announcements: popup=true first, then by start_date descending within each group
    undismissedAnnouncements.sort((a, b) => {
      // First sort by popup property (true comes before false/undefined)
      const popupA = a.popup === true ? 1 : 0;
      const popupB = b.popup === true ? 1 : 0;

      if (popupB !== popupA) {
        return popupB - popupA;
      }

      // If popup is the same, sort by start_date descending (most recent first)
      const dateA = new Date(a.startDate);
      const dateB = new Date(b.startDate);
      return dateB.getTime() - dateA.getTime();
    });

    // Store current announcements (all of them, not just popup ones)
    currentAnnouncements = undismissedAnnouncements;

    // Build and inject modal HTML with all undismissed announcements
    const modalHTML = buildModalHTML(undismissedAnnouncements);
    const container = document.createElement("div");
    container.innerHTML = modalHTML;

    const backdrop = container.firstElementChild;
    const modal = container.lastElementChild;
    if (backdrop) document.body.appendChild(backdrop);
    if (modal) document.body.appendChild(modal);

    // Setup event listeners
    setupEventListeners();

    // Show the modal
    showModal();
  } catch (error) {
    console.error("Error initializing announcements:", error);
  }
}

// Check if there are active announcements and show/hide header link
async function checkAnnouncementsForHeader(): Promise<void> {
  try {
    const announcements = await getAnnouncements();
    const activeAnnouncements = filterActiveAnnouncements(announcements);

    const headerLink = document.getElementById("announcements-link-container");
    if (headerLink) {
      if (activeAnnouncements.length > 0) {
        headerLink.style.display = "inline-block";
      } else {
        headerLink.style.display = "none";
      }
    }

    const navLink = document.getElementById("announcements-nav-container");
    if (navLink) {
      if (activeAnnouncements.length > 0) {
        navLink.style.display = "inline-block";
      } else {
        navLink.style.display = "none";
      }
    }
  } catch (error) {
    console.error("Error checking announcements for header:", error);
  }
}

// Initialize on DOM ready
export function initializeAnnouncements(): void {
  document.addEventListener("DOMContentLoaded", function () {
    init(); // Initialize popup
    checkAnnouncementsForHeader(); // Check and show header link
  });
}

function buildAnnouncementCardDiv(announcement: Announcement): HTMLDivElement {
  const card = document.createElement("div");
  card.className = "announcement-card";

  const title = document.createElement("h5");
  title.className = "announcement-card-title";
  title.textContent = announcement.title;
  card.appendChild(title);

  const message = document.createElement("p");
  message.className = "announcement-card-message";
  message.textContent = announcement.message;
  card.appendChild(message);

  if (announcement.link && announcement.linkText) {
    const link = document.createElement("a");
    link.className = "announcement-card-link";
    link.href = announcement.link;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.textContent = announcement.linkText;

    const linkWrapper = document.createElement("p");
    linkWrapper.appendChild(link);
    card.appendChild(linkWrapper);
  }

  return card;
}

function buildNoAnnouncementsMessage(): HTMLParagraphElement {
  const message = document.createElement("p");
  message.className = "no-announcements-message";
  message.textContent = "There are no club announcements at this time.";
  return message;
}

export function fillAnnouncements(selector: string): void {
  getAnnouncements().then((announcements) => {
    if (announcements === null) {
      fillErrorAnnouncements(selector);
      return;
    }

    const container = document.querySelector<HTMLElement>(selector);
    if (!container) return;

    container.innerHTML = ""; // Clear existing content

    const active = filterActiveAnnouncements(announcements);
    if (active.length === 0) {
      container.appendChild(buildNoAnnouncementsMessage());
      return;
    }

    // Sort by start_date descending (most recent first)
    active.sort((a, b) => {
      const dateA = new Date(a.startDate);
      const dateB = new Date(b.startDate);
      return dateB.getTime() - dateA.getTime();
    });

    active.forEach((announcement) => {
      const div = buildAnnouncementCardDiv(announcement);
      container.appendChild(div);
    });
  });
}

function fillErrorAnnouncements(selector: string): void {
  const container = document.querySelector<HTMLElement>(selector);
  if (!container) return;

  container.innerHTML = "<p>Failed to load announcements. Try again later.</p>";
}
